import React from 'react'
import { moneyOrBlank, signTone } from './overview'
import { fmtDay } from '../financial/formatting'
import DetailNote from './DetailNote'

// ─────────────────────────────────────────────────────────────────────────────
//   Los cuatro KPI destacados, en una sola fila
//
//   No los calcula esta pantalla ni salen de sus libros: `collected` son los
//   cobros registrados en Accounts receivable y `paid_to_affiliates` los pagos
//   registrados en Accounts payable, ambos computados por `financial/` desde
//   los propios leads.
//
//   El color es el mismo que usan Accounts receivable y Accounts payable, y
//   significa lo mismo que allí: verde el dinero que efectivamente se movió.
//   Los dos primeros son cobros y pagos REGISTRADOS —dinero que llegó y dinero
//   que salió— así que los dos son verdes, igual que "Collected" en una pantalla
//   y "Paid" en la otra.
//
// ─────────────────────────────────────────────────────────────────────────────
//   El tercer KPI ya no es el margen bruto
//
//   Era `gross_margin` —cobros menos pagos a afiliados— con el rótulo "Gross
//   margin", y era la única cifra de la pantalla con aspecto de resultado. Un
//   mes decía "$5,905.49" mientras la posición real de la empresa era
//   −$204.087,45, y se leyó como ganancia. El margen no estaba mal: TODO lo que
//   la empresa gasta para operar ocurre por debajo de esa línea. Era un rótulo
//   equivocado sobre una cifra correcta.
//
//   Ahora ese hueco lo ocupa `position.distributable`, que es la cifra que de
//   verdad contesta "¿cuánto hay?": el backend la calcula con un suelo en cero
//   y NUNCA es negativa.
//
//   **Ese 0 es una respuesta, no un hueco**, y es la única cifra de esta
//   pantalla donde el cero significa eso. En el resto de `/corporate/overview`
//   —la reserva de impuestos, los cinco tramos de la cascada— un null significa
//   "no se pudo calcular" y jamás se pinta como 0, porque son respuestas
//   opuestas (ver la cabecera de `overview.js`). Aquí es al revés y el backend
//   lo documenta a propósito: mientras la posición neta esté en rojo no hay
//   genuinamente nada que repartir, y eso es un hecho calculado. El tamaño del
//   agujero no se esconde —está con su signo en `net_position`, al lado—, así
//   que el copy del cero tiene que DECIRLO en vez de dejar un '$0.00' solo.
//
//   `gross_margin` no desaparece: sigue siendo una cifra real y útil sobre el
//   negocio de leads, y el backend la mantiene por eso. Baja a nota al pie de
//   la tarjeta, en gris y en pequeño, que es donde una cifra que NO es el
//   resultado puede vivir sin que nadie la lea como si lo fuera.
//
// ─────────────────────────────────────────────────────────────────────────────
//   Y el CUARTO, que son dos cifras de clases distintas
//
//   Era una tarjeta suelta debajo de la fila —`NetPositionPanel`— y ahí se leía
//   como otra sección, cuando es la respuesta a la misma pregunta que las tres
//   de al lado. Sube a la fila, partida en dos:
//
//     · arriba y con el peso de los otros tres, `deficit_period`: un FLUJO de
//       la ventana en pantalla. Sigue el filtro de rango y se rotula con él.
//     · abajo y más pequeño, `net_position`: una POSICIÓN. NO sigue el filtro
//       —enero y enero-a-agosto devuelven el mismo número— y trae su propio
//       `as_of` justamente para que una pantalla tenga con qué rotularla.
//       Ponerle el rango sería mentir sobre a qué periodo se refiere, que es el
//       mismo error del que viene todo esto. Es el mismo motivo por el que
//       `bank_debt` trae el suyo.
//
//   El por qué de la fórmula lo cuenta el `detail` del endpoint y NUNCA una
//   frase escrita aquí. Esta tarjeta llevaba a mano "less the bank debt still
//   owed"; el backend dejó de restar la deuda —los $111.000 del crédito entraron
//   y respaldan gasto que ya está contado como salida, así que cuentan como
//   capital y el saldo pendiente no se resta otra vez— y la frase se quedó
//   mintiendo sobre una cifra correcta durante semanas. `loan_principal_to_date`
//   sustituyó a `bank_debt_outstanding` en el esquema por lo mismo.
// ─────────────────────────────────────────────────────────────────────────────

function KpiCard({ tone, emphasis, children }) {
  return (
    <div
      className="card"
      style={{
        margin: 0, minWidth: 0, display: 'flex', flexDirection: 'column',
        // El borde superior marca la sección con el mismo token que la cifra,
        // así que la tarjeta se lee de un vistazo aunque el número esté lejos.
        borderTop: `2px solid ${tone || (emphasis ? 'var(--accent)' : 'transparent')}`,
      }}
    >
      {children}
    </div>
  )
}

function KpiLabel({ children }) {
  return (
    <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {children}
    </div>
  )
}

// La cifra grande: la misma altura en las cuatro tarjetas, que es lo que hace
// que la fila se lea como una sola respuesta y no como cuatro bloques.
function KpiValue({ value, tone }) {
  return (
    <div style={{ fontSize: 26, fontWeight: 700, color: tone || 'var(--text-primary)', marginTop: 4 }}>
      {value}
    </div>
  )
}

// Una línea, y una sola. El párrafo que explicaba cada KPI queda en el `detail`
// del endpoint, plegado — ver la cabecera de `DetailNote`.
function KpiLine({ children }) {
  return (
    <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, lineHeight: 1.5 }}>
      {children}
    </div>
  )
}

function Kpi({ label, line, value, tone, emphasis, footnote }) {
  return (
    <KpiCard tone={tone} emphasis={emphasis}>
      <KpiLabel>{label}</KpiLabel>
      <KpiValue value={value} tone={tone} />
      <KpiLine>{line}</KpiLine>
      {footnote && (
        <div
          style={{
            fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.5,
            marginTop: 'auto', paddingTop: 8, borderTop: '1px solid var(--border)',
          }}
        >
          {footnote}
        </div>
      )}
    </KpiCard>
  )
}

// La cuarta tarjeta. Dos cifras apiladas y con pesos distintos a propósito: no
// son la misma clase de número, y el tamaño es lo primero que lo dice.
function StandingKpi({ position, rangeText, loading }) {
  const asOf = position?.as_of

  return (
    <KpiCard tone={loading ? undefined : signTone(position?.deficit_period)}>
      <KpiLabel>Surplus or deficit</KpiLabel>
      {/* `signTone` devuelve undefined sin cifra: sin signo no hay color, y un
          verde "por no ser negativo" afirmaría que se calculó. */}
      <KpiValue
        value={loading ? '—' : moneyOrBlank(position?.deficit_period)}
        tone={loading ? undefined : signTone(position?.deficit_period)}
      />
      <KpiLine>Over {rangeText} — the only figure here that follows the range.</KpiLine>

      {/* `marginTop: auto` sobre el pie ENTERO —la posición y su nota— y no
          sólo sobre la primera mitad: así la línea que separa las dos cifras
          queda a la misma altura que la que separa el margen bruto en la
          tarjeta de al lado, y las cuatro tarjetas comparten un solo pie. */}
      <div style={{ marginTop: 'auto' }}>
        <div style={{ paddingTop: 10, borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Net position
          </div>
          <div
            style={{
              fontSize: 17, fontWeight: 600, marginTop: 2,
              color: (loading ? undefined : signTone(position?.net_position)) || 'var(--text-primary)',
            }}
          >
            {loading ? '—' : moneyOrBlank(position?.net_position)}
          </div>
          {/* La fecha es la del backend y NUNCA el rango: una posición y un
              flujo responden preguntas distintas, y rotular ésta con la ventana
              diría que cambia con ella cuando no lo hace. */}
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.45 }}>
            {asOf
              ? <>The whole of the books, as of {fmtDay(asOf)}.</>
              : <>The whole of the books, not a total of the range.</>}
          </div>
        </div>

        {/* El `detail` del propio endpoint, literal y plegado. Dice cómo se
            compone la posición mejor de lo que puede decirlo esta pantalla — y
            ya se comprobó lo que cuesta escribirlo aquí a mano. */}
        <DetailNote
          style={{ marginTop: 10 }}
          summary="How these two differ"
          detail={position?.detail}
        />
      </div>
    </KpiCard>
  )
}

export default function HeadlineKpis({ headline, position, rangeText, loading }) {
  const blank = loading ? '—' : undefined
  const distributable = position?.distributable
  const known = distributable !== null && distributable !== undefined

  return (
    <div style={{ marginBottom: 16 }}>
      <div
        className="cf-kpis"
        style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, alignItems: 'stretch' }}
      >
        <Kpi
          label="Collected from buyers"
          value={blank ?? moneyOrBlank(headline?.collected)}
          // Verde sin mirar el signo: un cobro registrado es dinero que llegó.
          // No se pinta mientras carga, que es cuando no hay cifra que teñir.
          tone={loading ? undefined : 'var(--green)'}
          line="Registered in Accounts receivable — money that arrived."
        />
        <Kpi
          label="Paid to affiliates"
          value={blank ?? moneyOrBlank(headline?.paid_to_affiliates)}
          tone={loading ? undefined : 'var(--green)'}
          line="Registered in Accounts payable — money that left."
        />
        <Kpi
          emphasis
          label="Distributable"
          value={blank ?? moneyOrBlank(distributable)}
          // Verde sólo cuando hay algo que repartir. El cero NO va en verde
          // —no es dinero a favor— pero tampoco en gris de "sin dato": se queda
          // en el color del texto, que es el que usa una cifra cierta que no
          // dice ni bien ni mal. Y nunca en rojo: esta cifra no baja de cero,
          // y el hueco con su signo está en `net_position`, al lado.
          tone={loading || !known ? undefined : distributable > 0 ? 'var(--green)' : 'var(--text-primary)'}
          line={
            known && distributable === 0
              // El cero se explica en el propio KPI. Un '$0.00' a secas es
              // indistinguible de un dato que no se pudo calcular, y ésa es la
              // lectura que este bloque existe para impedir.
              ? 'Nothing is distributable yet — this 0 is the answer, not a missing figure.'
              : 'What is available to distribute, once everything spent is taken off.'
          }
          footnote={
            <>
              Gross margin is{' '}
              <strong style={{ color: 'var(--text-secondary)' }}>
                {blank ?? moneyOrBlank(headline?.gross_margin)}
              </strong>{' '}
              — not what the company has left.
            </>
          }
        />
        <StandingKpi position={position} rangeText={rangeText} loading={loading} />
      </div>
    </div>
  )
}
